import i18n, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next, useTranslation as useI18nextTranslation } from 'react-i18next';
import { publicAr, type PublicTranslations } from './public-ar';
import { publicHe } from './public-he';
import { publicEn } from './public-en';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  directionForLocale,
  type SupportedLocale,
} from './direction';
import type { TranslationKey } from './types';

export type { TranslationKey, SupportedLocale };
export { directionForLocale, SUPPORTED_LOCALES, DEFAULT_LOCALE };
export {
  PERSISTED_LOCALE_STORAGE_KEY,
  isSupportedLocale,
  normalizeLocale,
  readPersistedLocale,
  readStoredLocalePreference,
  writePersistedLocale,
} from './persistence';

export const PUBLIC_I18N_RESOURCES = {
  ar: publicAr,
  he: publicHe,
  en: publicEn,
} as const;

const syncedResourceRefs = new WeakMap<
  I18nInstance,
  {
    readonly ar: PublicTranslations;
    readonly he: PublicTranslations;
    readonly en: PublicTranslations;
  }
>();

export interface InitI18nOptions {
  locale?: SupportedLocale;
}

function syncResourceBundles(instance: I18nInstance, force = false): void {
  const refs = syncedResourceRefs.get(instance);
  if (!force && refs?.ar === publicAr && refs.he === publicHe && refs.en === publicEn) return;

  (
    Object.entries(PUBLIC_I18N_RESOURCES) as Array<
      [SupportedLocale, (typeof PUBLIC_I18N_RESOURCES)[SupportedLocale]]
    >
  ).forEach(([locale, translation]) => {
    instance.addResourceBundle(locale, 'translation', translation, true, true);
  });

  syncedResourceRefs.set(instance, { ar: publicAr, he: publicHe, en: publicEn });
}

export function initI18n(opts: InitI18nOptions = {}): I18nInstance {
  if (!i18n.isInitialized) {
    void i18n.use(initReactI18next).init({
      resources: Object.fromEntries(
        (
          Object.entries(PUBLIC_I18N_RESOURCES) as Array<
            [SupportedLocale, (typeof PUBLIC_I18N_RESOURCES)[SupportedLocale]]
          >
        ).map(([locale, translation]) => [locale, { translation }]),
      ),
      lng: opts.locale ?? DEFAULT_LOCALE,
      fallbackLng: 'en',
      // M3: escape interpolated values by default. React already escapes
      // children, but interpolated variables inside i18next strings (e.g.
      // `t('profile.hello', { name })`) bypass React escaping, so we must
      // escape them here. The `<Trans>` component is the escape hatch for
      // HTML-in-translation cases; any rich-text render must additionally
      // pass through DOMPurify.
      //
      // F-015 (devil W6): `format` callback forces Latin digits for any
      // numeric interpolation (e.g. `{{count}}` in plural keys). Arabic
      // plural keys render "5 عناصر" not "٥ عناصر" — keeps the visible
      // badge pill ("5") and the aria-label character-for-character
      // aligned. Any future plural key with `{{count}}` inherits this
      // automatically.
      interpolation: {
        escapeValue: true,
        format: (value, format) => {
          if (format === 'number' || typeof value === 'number') {
            return Number(value).toLocaleString('en');
          }
          return value as string;
        },
      },
      returnNull: false,
    });
  }
  if (i18n.isInitialized) syncResourceBundles(i18n, true);
  return i18n;
}

export function refreshI18nResources(instance: I18nInstance = i18n): void {
  if (instance.isInitialized) syncResourceBundles(instance);
}

export function installPublicI18nResources(instance: I18nInstance = i18n): void {
  if (instance.isInitialized) syncResourceBundles(instance, true);
}

export function useTranslation(): {
  t: (key: TranslationKey, options?: Record<string, unknown>) => string;
  dir: 'rtl' | 'ltr';
  lang: SupportedLocale;
  i18n: I18nInstance;
} {
  const { t: rawT, i18n: instance } = useI18nextTranslation();
  const lang = (instance.language ?? DEFAULT_LOCALE) as SupportedLocale;
  const dir = directionForLocale(lang);
  const t = (key: TranslationKey, options?: Record<string, unknown>): string =>
    rawT(key, options) as string;
  return { t, dir, lang, i18n: instance };
}
